import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { clientHasPartnerWithDemographics } from '@shared';
import { selectClient, selectMaybeClientPartner } from '@/model';
import type { PaymentOption, BuyerOption, ShopInsuranceMode } from '@/features/shop-insurance';

export const BUYER_OPTIONS: Record<'1-buyer' | '2-buyer', BuyerOption> = {
  '1-buyer': {
    key: '1-buyer',
    label: '1 Buyer',
    maritalClass: 'Married 1 Buy',
  },
  '2-buyer': {
    key: '2-buyer',
    label: '2 Buyers',
    maritalClass: 'Married 2 Buy',
  },
};

interface UseShopInsuranceStateProps {
  hideSinglePayAnd10Pay?: boolean;
  providedSelectedTab?: ShopInsuranceTab;
  providedPaymentOption?: PaymentOption;
  providedBuyerOption?: '1-buyer' | '2-buyer';
  providedMode?: ShopInsuranceMode;
}

export function useShopInsuranceState(props?: UseShopInsuranceStateProps) {
  const { hideSinglePayAnd10Pay = false } = props || {};
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);

  const hasPartnerWithDemographics = clientHasPartnerWithDemographics(client, clientPartner);

  const hasSameGenderPartner = useMemo(() => {
    return Boolean(
      client?.partnerClientId &&
      clientPartner?.intakeSurvey?.clientGenderAtBirth &&
      client?.intakeSurvey?.clientGenderAtBirth &&
      client?.intakeSurvey?.clientGenderAtBirth ===
        clientPartner?.intakeSurvey?.clientGenderAtBirth,
    );
  }, [
    client?.partnerClientId,
    client?.intakeSurvey?.clientGenderAtBirth,
    clientPartner?.intakeSurvey?.clientGenderAtBirth,
  ]);

  const defaultPaymentOption: PaymentOption = props?.providedPaymentOption
    ? props?.providedPaymentOption
    : hideSinglePayAnd10Pay
      ? 'Annually'
      : 'Single Pay';

  const [selectedTab, setSelectedTab] = useState<ShopInsuranceTab>(
    props?.providedSelectedTab
      ? props?.providedSelectedTab
      : hasPartnerWithDemographics
        ? 'joint'
        : 'single',
  );
  const [selectedBuyerOption, setSelectedBuyerOption] = useState<BuyerOption>(
    props?.providedBuyerOption
      ? BUYER_OPTIONS[props?.providedBuyerOption]
      : BUYER_OPTIONS['1-buyer'],
  );
  const [paymentOption, setPaymentOption] = useState<PaymentOption>(defaultPaymentOption);
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);
  const [mode, setMode] = useState<ShopInsuranceMode>(
    props?.providedMode ?? 'premium-optimization',
  );

  useEffect(() => {
    if (!props?.providedSelectedTab) {
      const newTab = hasPartnerWithDemographics ? 'joint' : 'single';
      setSelectedTab(newTab);
    }
  }, [client?.clientId, hasPartnerWithDemographics, props?.providedSelectedTab]);

  const [showPartnerRequiredDialog, setShowPartnerRequiredDialog] = useState(false);

  const handleTabChange = (tabValue: string): boolean => {
    if (tabValue === 'joint' && !hasPartnerWithDemographics) {
      setShowPartnerRequiredDialog(true);
      return false;
    }
    setSelectedTab(tabValue as ShopInsuranceTab);
    setExpandedIndices([]);
    return true;
  };

  const handleCardToggle = (index: number) => {
    setExpandedIndices(currentIndices => {
      if (currentIndices.includes(index)) {
        return currentIndices.filter(expandedIndex => expandedIndex !== index);
      } else {
        return [...currentIndices, index];
      }
    });
  };

  const handleAcknowledgment = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      localStorage.setItem('shopInsuranceAcknowledgmentDismissed', 'true');
    }
    setHasAcknowledged(true);
    setShowAcknowledgment(false);
  };

  const handlePaymentOptionChange = (value: PaymentOption) => {
    setPaymentOption(value);
  };

  const handleModeChange = (value: ShopInsuranceMode) => {
    setMode(value);
  };

  const handleBuyerOptionChange = (buyerKey: '1-buyer' | '2-buyer') => {
    setSelectedBuyerOption(BUYER_OPTIONS[buyerKey]);
  };

  const resetState = useCallback(() => {
    setExpandedIndices([]);
    setHasAcknowledged(false);
  }, []);

  return {
    selectedTab,
    setSelectedTab,
    selectedBuyerOption,
    paymentOption,
    mode,
    expandedIndices,
    setExpandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    hasPartnerWithDemographics,
    hasSameGenderPartner,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    setShowAcknowledgment,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    handlePaymentOptionChange,
    handleModeChange,
    handleBuyerOptionChange,
    resetState,
  };
}
